import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getNotifications, markNotificationRead, markAllNotificationsRead, getCurrentUserProfile } from '../services/api';
import { getDateLocale } from '../i18n';
import JiraTicketSidebarContent from './JiraTicketSidebarContent';
import JiraCommentSidebarContent from './JiraCommentSidebarContent';

const DEFAULT_SIDEBAR_WIDTH = 320;
const MIN_SIDEBAR_WIDTH = 240;
const MAX_SIDEBAR_WIDTH = 560;
const SIDEBAR_WIDTH_KEY = 'notificationSidebarWidth';
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const DATE_OPTION_KEYS = [
  { value: '', labelKey: 'allDates' },
  { value: 'today', labelKey: 'today' },
  { value: 'yesterday', labelKey: 'yesterday' },
  { value: 'this_week', labelKey: 'thisWeek' },
  { value: 'this_month', labelKey: 'thisMonth' },
  { value: 'custom', labelKey: 'custom' },
];

const getDateRangeFromOption = (option, customFrom, customTo) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (option === 'today') {
    const d = today.toISOString().slice(0, 10);
    return { date_from: d, date_to: d };
  }
  if (option === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const d = yesterday.toISOString().slice(0, 10);
    return { date_from: d, date_to: d };
  }
  if (option === 'this_week') {
    const weekStart = new Date(today);
    const day = weekStart.getDay();
    const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
    weekStart.setDate(diff);
    const date_from = weekStart.toISOString().slice(0, 10);
    const date_to = today.toISOString().slice(0, 10);
    return { date_from, date_to };
  }
  if (option === 'this_month') {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const date_from = monthStart.toISOString().slice(0, 10);
    const date_to = today.toISOString().slice(0, 10);
    return { date_from, date_to };
  }
  if (option === 'custom' && customFrom && customTo) {
    return { date_from: customFrom, date_to: customTo };
  }
  return { date_from: undefined, date_to: undefined };
};

const getStoredWidth = () => {
  try {
    const w = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY), 10);
    if (Number.isFinite(w) && w >= MIN_SIDEBAR_WIDTH && w <= MAX_SIDEBAR_WIDTH) return w;
  } catch (_) {}
  return DEFAULT_SIDEBAR_WIDTH;
};

const NotificationSidebar = ({
  isOpen,
  onClose,
  onUnreadCountChange,
  jiraTicketDetail = null,
  jiraCommentDetail = null,
  onJiraTicketUpdated,
  onJiraCommentImputationInserted,
}) => {
  const { t, i18n } = useTranslation('notifications');
  const { t: tCommon } = useTranslation('common');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [dateFilterOption, setDateFilterOption] = useState('');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sidebarWidth, setSidebarWidth] = useState(getStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const searchDebounceRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, width: 0 });

  const fetchUnreadCount = useCallback(async () => {
    try {
      const profile = await getCurrentUserProfile();
      const count = profile.unread_notification_count || 0;
      setUnreadCount(count);
      onUnreadCountChange?.(count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [onUnreadCountChange]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const { date_from, date_to } = getDateRangeFromOption(dateFilterOption, customDateFrom, customDateTo);
      const filters = {
        page,
        page_size: pageSize,
        search: searchFilter || undefined,
        date_from,
        date_to,
      };
      const data = await getNotifications(filters);
      const notificationsList = data.results || data || [];
      const list = Array.isArray(notificationsList) ? notificationsList : [];
      setNotifications(list);
      setTotalCount(data.count ?? list.length);
      setTotalPages(data.total_pages ?? 1);

      const unreadIds = list.filter((n) => !n.is_read).map((n) => n.id);
      if (unreadIds.length > 0) {
        await Promise.all(unreadIds.map((id) => markNotificationRead(id)));
        setNotifications((prev) =>
          prev.map((n) => (unreadIds.includes(n.id) ? { ...n, is_read: true } : n))
        );
        await fetchUnreadCount();
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchFilter, dateFilterOption, customDateFrom, customDateTo, fetchUnreadCount]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen && !jiraTicketDetail && !jiraCommentDetail) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [isOpen, jiraTicketDetail, jiraCommentDetail, fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (!isOpen || (!jiraTicketDetail && !jiraCommentDetail)) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, jiraTicketDetail, jiraCommentDetail, onClose]);

  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchFilter, pageSize, dateFilterOption, customDateFrom, customDateTo]);

  // Clamp page when totalPages decreases (e.g. after filtering)
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // Debounced search - update searchFilter after 300ms of no typing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchFilter(value);
      searchDebounceRef.current = null;
    }, 300);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      setSearchFilter(searchInputValue);
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, width: sidebarWidth };
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e) => {
      const deltaX = resizeStartRef.current.x - e.clientX;
      const newWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, resizeStartRef.current.width + deltaX));
      setSidebarWidth(newWidth);
      try {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(newWidth));
      } catch (_) {}
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
      onUnreadCountChange?.(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return t('minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('daysAgo', { count: diffDays });
    return date.toLocaleDateString(getDateLocale(i18n.language), { month: 'short', day: 'numeric' });
  };

  const currentWidth = isOpen ? sidebarWidth : 0;
  const showJiraTicketDetail = Boolean(isOpen && jiraTicketDetail);
  const showJiraCommentDetail = Boolean(isOpen && jiraCommentDetail);
  const showJiraDetail = showJiraTicketDetail || showJiraCommentDetail;
  const rangeFrom = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const rangeTo = totalCount > 0 ? Math.min(page * pageSize, totalCount) : 0;

  return (
    <motion.aside
      initial={false}
      animate={{
        width: currentWidth,
      }}
      transition={isResizing ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 30 }}
      className="h-full flex flex-col bg-surface border-l border-border overflow-hidden shrink-0 relative"
      style={{ minWidth: 0 }}
    >
      {/* Resize handle - left edge when sidebar is on the right */}
      {isOpen && (
        <div
          role="separator"
          aria-label={tCommon('resizePanel')}
          onMouseDown={handleResizeStart}
          className={`absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-10 -ml-1 flex items-center justify-center group hover:bg-primary/10 transition-colors ${isResizing ? 'bg-primary/20' : ''}`}
        >
          <div className="w-0.5 h-12 rounded-full bg-slate-300 dark:bg-slate-600 group-hover:bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-primary">
            {showJiraTicketDetail ? t('ticketDetails') : (showJiraCommentDetail ? t('commentDetails') : t('title'))}
          </h3>
          <div className="flex items-center gap-2">
            {!showJiraDetail && unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                {t('markAllRead')}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-md text-text-secondary hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
              aria-label={showJiraDetail ? t('closeDetails') : t('closeNotifications')}
            >
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>
        </div>

        {/* Filter — notifications only */}
        {!showJiraDetail && (
        <div className="px-3 py-2 border-b border-border shrink-0 space-y-2">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[18px] text-text-secondary">search</span>
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchInputValue}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <div>
            <select
              value={dateFilterOption}
              onChange={(e) => setDateFilterOption(e.target.value)}
              className="w-full py-2 px-3 text-sm border border-border rounded-lg bg-background text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {DATE_OPTION_KEYS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
            {dateFilterOption === 'custom' && (
              <div className="mt-2 space-y-2">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">{t('from')}</label>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="w-full py-2 px-3 text-sm border border-border rounded-lg bg-background text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">{t('to')}</label>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="w-full py-2 px-3 text-sm border border-border rounded-lg bg-background text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Jira ticket or notifications list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {showJiraTicketDetail ? (
            <JiraTicketSidebarContent
              ticket={jiraTicketDetail}
              onTicketUpdated={onJiraTicketUpdated}
            />
          ) : showJiraCommentDetail ? (
            <JiraCommentSidebarContent
              comment={jiraCommentDetail}
              onCommentImputationInserted={onJiraCommentImputationInserted}
            />
          ) : loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-text-secondary text-sm">
              <span className="material-symbols-outlined text-3xl mb-2 block text-slate-300 dark:text-slate-600">notifications_off</span>
              <p>{t('noNotifications')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                      !notification.is_read ? 'bg-blue-500' : 'bg-transparent'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-semibold mb-1 ${
                        !notification.is_read ? 'text-primary' : 'text-text-secondary'
                      }`}>
                        {notification.title}
                      </h4>
                      <p className="text-xs text-text-secondary line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination — notifications only */}
        {!showJiraDetail && !loading && (
          <div className="px-3 py-2 border-t border-border shrink-0 space-y-2">
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span>
                {totalCount > 0
                  ? t('rangeOfTotal', { from: rangeFrom, to: rangeTo, total: totalCount })
                  : t('zeroOfTotal', { total: totalCount })}
              </span>
              <div className="flex items-center gap-1">
                <label className="text-text-secondary">{t('perPage')}</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="py-1 px-2 text-sm border border-border rounded bg-background text-primary focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex-1 py-1.5 text-xs font-medium rounded border border-border bg-background text-primary hover:bg-slate-100 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              >
                {t('previous')}
              </button>
              <span className="text-xs text-text-secondary shrink-0">
                {t('pageOf', { page, totalPages: totalPages || 1 })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= (totalPages || 1)}
                className="flex-1 py-1.5 text-xs font-medium rounded border border-border bg-background text-primary hover:bg-slate-100 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              >
                {t('next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.aside>
  );
};

export default NotificationSidebar;
